import {CollapsibleSection} from './CollapsibleSection';
import {SegmentGroup} from './SegmentGroup';
import {ShipmentTree} from './ShipmentTree';
import {buildSections, countIssues, groupBasePaths} from './lib/sections';
import {
  ak2RowTitle,
  ak3RowTitle,
  lineTitle,
  lineTitle810,
  lineTitle840,
  lineTitle843,
  lineTitle844,
  lineTitle845,
  lineTitle852,
  lineTitle855,
  lineTitle867,
  lineTitle210,
  chargeLineTitle210,
  partyTitle,
  sectionPreview,
} from './lib/rowTitles';
import type {SectionGroup} from './lib/sections';
import type {EdiTransactionExplained} from './lib/explainedEnvelope';
import type {ElementPath} from './lib/document-editor';
import type {ValidationFinding} from './lib/validator';

type LineTitleFn = (
  group: SectionGroup,
  index: number
) => {title: string; preview: string};

const LINE_TITLE_BY_TRANSACTION_SET: Record<string, LineTitleFn> = {
  '852': lineTitle852,
  '855': lineTitle855,
  '840': lineTitle840,
  '843': lineTitle843,
  '810': lineTitle810,
  '844': lineTitle844,
  '845': lineTitle845,
  '867': lineTitle867,
  '210': lineTitle210,
};

interface DataSectionsProps {
  groupIndex: number;
  transactionIndex: number;
  transaction: EdiTransactionExplained;
  findings: ValidationFinding[];
  onEdit: (path: ElementPath, value: string) => void;
}

/**
 * The active 210, 810, 840, 843, 844, 845, 850, 852, 855, 856, 867 or 997
 * transaction, organized by kind of data (see lib/sections.ts) as
 * collapsible panels. Parties, line/activity items, the 856's Shipments
 * tree and the 210's Stops > charge-line tree are each one collapsible row.
 * Every collapsed header shows its error/warning counts up front.
 */
export function DataSections({
  groupIndex,
  transactionIndex,
  transaction,
  findings,
  onEdit,
}: DataSectionsProps) {
  const sections = buildSections(transaction, groupIndex, transactionIndex);

  const renderEntries = (group: SectionGroup) => (
    <div className="divide-y divide-border/60">
      {group.entries.map(({segment, basePath}) => (
        <SegmentGroup
          key={basePath}
          segment={segment}
          basePath={basePath}
          groupIndex={groupIndex}
          transactionIndex={transactionIndex}
          transaction={transaction}
          findings={findings}
          onEdit={onEdit}
        />
      ))}
    </div>
  );

  return (
    <div className="space-y-2">
      {sections.map(section => (
        <CollapsibleSection
          key={section.key}
          title={section.title}
          preview={sectionPreview(section)}
          tooltip={section.description}
          issues={countIssues(findings, groupBasePaths(section.groups))}
        >
          {section.key === 'shipments' ? (
            <div className="space-y-1.5">
              {section.groups.map(group => (
                <ShipmentTree
                  key={group.key}
                  group={group}
                  groupIndex={groupIndex}
                  transactionIndex={transactionIndex}
                  transaction={transaction}
                  findings={findings}
                  onEdit={onEdit}
                />
              ))}
            </div>
          ) : section.repeating ? (
            <div className="space-y-1.5">
              {section.groups.map((group, i) => {
                const {title, preview} =
                  section.key === 'parties'
                    ? partyTitle(group)
                    : section.key === 'transactionSetAcks'
                      ? ak2RowTitle(group, i)
                      : (
                          LINE_TITLE_BY_TRANSACTION_SET[
                            transaction.transactionSetCode
                          ] ?? lineTitle
                        )(group, i);
                return (
                  <CollapsibleSection
                    key={group.key}
                    level="row"
                    title={title}
                    preview={preview}
                    issues={countIssues(findings, groupBasePaths([group]))}
                  >
                    {renderEntries(group)}
                    {group.subgroups && group.subgroups.length > 0 && (
                      <div className="space-y-1.5 border-t border-border/60 p-2 pl-4">
                        {group.subgroups.map(subgroup => {
                          const ak3 =
                            section.key === 'stops'
                              ? chargeLineTitle210(subgroup)
                              : ak3RowTitle(subgroup);
                          return (
                            <CollapsibleSection
                              key={subgroup.key}
                              level="row"
                              title={ak3.title}
                              preview={ak3.preview}
                              issues={countIssues(
                                findings,
                                groupBasePaths([subgroup])
                              )}
                            >
                              {renderEntries(subgroup)}
                            </CollapsibleSection>
                          );
                        })}
                      </div>
                    )}
                  </CollapsibleSection>
                );
              })}
            </div>
          ) : (
            renderEntries(section.groups[0])
          )}
        </CollapsibleSection>
      ))}
    </div>
  );
}
